import React, { useEffect, useRef, useState } from "react";
import "./AgencyOnboardingPostView.scss";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../../enums/common.enums";
import toast from "react-hot-toast";
import { clientRoutes } from "../../../../../utils/routes";
import { Modal, CircularProgress } from "@mui/material";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import PreviewModal from "../../propertypost/view/PreviewModal";
import Loading from "../../../../loading/Loading";
import Upload from "../../../../../components/icons/Upload";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { PropertyApi } from "../../../../../utils/api/Property";
import clsx from "clsx";
import OnboardingPostConfiguration from "./onboardingPostConfiguration";
import OnBoardingListMenuIcon from "./OnBoardingListMenuIcon";
import UpdateTemplateModal from "../../../WorkspaceSheduleConfigure/UpdateTemplateModal";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

interface InputField {
  handler: string;
  label: string;
  value: string;
  template_input_index: number;
  characters_limit?: number;
  input_type?: string;
  filepath?: string;
  title_input?: boolean;
  use_stage_name?: boolean;
  enabled?: boolean;
  use_main_image?: boolean;
}

interface OnboardingPostData {
  _id: string;
  review_type: string;
  review_on: string;
  post_status: string;
  post_type: string;
  user_type?: string;
  schedule_datetime: string | null;
  createdAt: string;
  updatedAt: string;
  inputs: Record<string, InputField>;
  design_template_id: string;
  listing_type?: string;
  vendorType?: string;
  image_post_design_activity_ref?: any;
  story_post_design_activity_ref?: any;
  video_post_design_activity_ref?: any;
  reel_post_design_activity_ref?: any;
  agent_ref?: any;
  agency_ref?: any;
}

const AgencyOnboardingPostView = () => {
  const navigate = useNavigate();
  const { onboardingPostId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(true);
  const [onboardingPost, setOnboardingPost] =
    useState<OnboardingPostData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [inputOrder, setInputOrder] = useState<string[]>([]);
  const [imageEditing, setImageEditing] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [draggedOverKey, setDraggedOverKey] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState<{
    [key: string]: boolean;
  }>({});

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const subscriptionType = searchParams.get("subscription_type") || "";
  const listingType =
    searchParams.get("listing_type") || onboardingPost?.listing_type || "sale";
  const postTypeFromUrl =
    searchParams.get("post_type") || onboardingPost?.post_type || "";

  useEffect(() => {
    const step = searchParams.get("step");
    if (step === "configure_post") {
      setActiveTab("postConfig");
    } else {
      setActiveTab("info");
    }
  }, [searchParams]);

  const handleFileUpdate = (file: File | undefined, key: string) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select or drop a valid image file.");
      setDraggedOverKey(null);
      return;
    }

    setIsImageLoading((prev) => ({ ...prev, [key]: true }));
    const url = URL.createObjectURL(file);

    setFormValues((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: url, file, original_path: "" },
    }));

    setImageEditing((prev) => ({ ...prev, [key]: false }));
    setDraggedOverKey(null);

    setTimeout(() => {
      setIsImageLoading((prev) => ({ ...prev, [key]: false }));
    }, 500);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatPostType = (type: string) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusBadge = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "Completed";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      case "pending_for_social_media_upload":
        return "Pending Upload";
      case "scheduled for post":
        return "Scheduled";
      case "cancelled":
        return "Cancelled";
      case "errored":
      case "error":
        return "Error";
      default:
        return "N/A";
    }
  };

  const getSelectedTemplateId = (
    postType: string | undefined,
    onboardingPost?: any
  ) => {
    const normalizedPostType = (postType || "").toLowerCase();
    switch (normalizedPostType) {
      case "image_post":
        return (
          onboardingPost?.image_post_design_activity_ref?.design_template_id
            ?._id ||
          onboardingPost?.design_template_id?._id ||
          null
        );
      case "story_post":
        return (
          onboardingPost?.story_post_design_activity_ref?.design_template_id
            ?._id ||
          onboardingPost?.design_template_id?._id ||
          null
        );
      default:
        return onboardingPost?.design_template_id?._id || null;
    }
  };

  const resolveOnboardingTemplateType = (
    postType?: string,
    listingType?: string
  ) => {
    const normalizedPostType = (postType || "").toLowerCase();
    const normalizedListingType = (listingType || "").toLowerCase();

    if (normalizedPostType === "image_post") {
      return "onboarding_for_sale_image_post";
    }
    if (normalizedPostType === "story_post") {
      return "onboarding_for_sale_story_post";
    }

    return undefined;
  };

  const fetchOnboardingTemplateId = async (
    subscriptionType: string,
    listingType: string,
    postType: string
  ) => {
    try {
      const response = await TemplatesApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
      });

      console.log("Template Configs Response:", response);

      if (!response?.success || !response.data?.data) {
        toast.error("Failed to load template configs!");
        return null;
      }

      const postTypeBlock = response.data.data[postType];
      if (!postTypeBlock || !postTypeBlock.templates) {
        console.warn(
          `No templates found for postType=${postType}`,
          postTypeBlock
        );
        return null;
      }

      const expectedSlug = resolveOnboardingTemplateType(postType, listingType);
      console.log("Looking for template with slug:", expectedSlug);
      console.log("Available templates:", postTypeBlock.templates);

      if (!expectedSlug) {
        console.warn(`Unsupported combination: ${listingType}, ${postType}`);
        return null;
      }

      const foundTemplate = postTypeBlock.templates.find(
        (tpl: any) =>
          tpl.slug === expectedSlug && tpl.config?.design_template?._id
      );

      console.log("Found template:", foundTemplate);

      if (!foundTemplate) {
        const fallbackTemplate = postTypeBlock.templates.find(
          (tpl: any) => tpl.config?.design_template?._id
        );
        if (fallbackTemplate) {
          console.log("Using fallback template:", fallbackTemplate);
          return fallbackTemplate.config.design_template._id;
        }
      }

      return foundTemplate?.config.design_template._id || null;
    } catch (err) {
      console.error("Error fetching template configs:", err);
      toast.error("Error fetching template configs!");
      return null;
    }
  };

  const fetchTemplate = async (forceDesignTemplateId?: string) => {
    setIsLoading(true);
    try {
      let existingGeneratedImage: string | null = null;
      let onboardingPostData: any = null;
      let designTemplateId: string | null = forceDesignTemplateId || null;

      if (onboardingPostId) {
        const onboardingRes = await PropertyApi.GetOnboardingPostById(
          onboardingPostId
        );
        onboardingPostData = onboardingRes?.data?.data;

        if (onboardingPostData) {
          setOnboardingPost(onboardingPostData);
          setLoading(false);

          const refs = [
            onboardingPostData?.image_post_design_activity_ref,
            onboardingPostData?.story_post_design_activity_ref,
          ].filter(Boolean);
          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (firstWithImage) {
            existingGeneratedImage = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
          }

          const effectivePostType =
            onboardingPostData.post_type || postTypeFromUrl;

          designTemplateId =
            designTemplateId ||
            onboardingPostData?.image_post_design_activity_ref
              ?.design_template_id?._id ||
            getSelectedTemplateId(effectivePostType, onboardingPostData);
        }
      }

      if (!designTemplateId) {
        console.log("Fetching template ID from configs...");

        const typeForConfig = onboardingPostData?.post_type || postTypeFromUrl;

        designTemplateId = await fetchOnboardingTemplateId(
          subscriptionType,
          listingType,
          typeForConfig
        );
      }

      console.log("Final designTemplateId:", designTemplateId);

      if (!designTemplateId) {
        toast.error("No matching onboarding template found!");
        setIsLoading(false);
        return;
      }

      const res = await TemplatesApi.getDesignTemplateById(designTemplateId);

      if (res.success && res.data) {
        const templateDetails = res.data.data;
        setTemplateData({ data: templateDetails });

        const templateFallbackImage =
          templateDetails.template_image_ref?.original_file_path ||
          templateDetails.template_image;
        setTemplateData((prev: any) => ({
          ...prev,
          generatedImage:
            existingGeneratedImage ||
            (templateFallbackImage
              ? `${API_BASE_URL}/${templateFallbackImage}`
              : null),
        }));

        const apiInputs = templateDetails.inputs || {};
        setInputs(apiInputs);
        setInputOrder(Object.keys(apiInputs));

        const existingInputs = onboardingPostData?.inputs || {};
        const newFormValues: { [key: string]: any } = {};

        Object.keys(apiInputs).forEach((key, index) => {
          const apiInput = apiInputs[key];
          const existingValue = existingInputs[key];
          const isImage = apiInput.input_type === "image";
          let value: any = "";
          let original_path: string | null = null;

          if (isImage) {
            original_path =
              existingValue?.filepath ?? apiInput.filepath ?? null;
            if (original_path) {
              value = CommonHelpers.getImageUrlFromImageTool({
                path: original_path,
              });
            }
          } else {
            value = existingValue?.value ?? apiInput.value ?? "";
          }

          newFormValues[key] = {
            value: value,
            original_path: original_path,
            file: null,
            handler: apiInput.handler || key,
            label: apiInput.label || key,
            template_input_index: apiInput.template_input_index ?? index,
            characters_limit: apiInput.characters_limit || 100,
            input_type: apiInput.input_type || "text",
            enabled: existingValue?.enabled ?? apiInput.enabled ?? true,
            use_main_image: isImage ? apiInput.use_main_image ?? false : false,
          };
        });

        setFormValues(newFormValues);
      } else {
        toast.error(res.error || "Failed to fetch template!");
      }
    } catch (err) {
      console.error("Error fetching template:", err);
      toast.error("Failed to fetch template data!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onboardingPostId) {
      fetchTemplate();
    }
  }, [onboardingPostId, subscriptionType, listingType]);

  const buildInputsPayload = async () => {
    const imageKeysWithFiles = Object.keys(formValues).filter(
      (key) => formValues[key]?.input_type === "image" && formValues[key]?.file
    );

    const uploadedFilePaths: Record<string, string> = {};

    if (imageKeysWithFiles.length > 0) {
      const uploadEndpoint = `${process.env.REACT_APP_DESIGN_TOOL_ENDPOINT}/api/uploads/upload-template-input-images`;
      const formData = new FormData();

      for (const key of imageKeysWithFiles) {
        const fileObj = formValues[key].file;
        if (!fileObj) continue;
        formData.append("input_image", fileObj, fileObj.name);
      }

      try {
        const response = await fetch(uploadEndpoint, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();

        if (data?.success && Array.isArray(data.data)) {
          data.data.forEach((filePathData: any, index: number) => {
            const key = imageKeysWithFiles[index];
            const path = filePathData?.filepath || filePathData?.path;
            if (path) uploadedFilePaths[key] = path;
          });
        } else {
          throw new Error("Bulk image upload response was not successful.");
        }
      } catch (uploadErr) {
        toast.error("Failed to upload image(s). Please try again.");
        return null;
      }
    }

    const inputsPayload: Record<string, any> = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (!val || !inputs[key]) return;

      if (val.input_type === "image") {
        inputsPayload[key] = {
          ...inputs[key],
          filepath: uploadedFilePaths[key] || val.original_path || "",
          enabled: val.enabled ?? true,
          use_main_image: !val.file && (val.use_main_image ?? false),
        };
      } else {
        inputsPayload[key] = {
          ...inputs[key],
          value: val.value,
          enabled: val.enabled ?? true,
        };
      }
    });
    return inputsPayload;
  };

  const handleApiSubmit = async () => {
    if (!onboardingPostId) return { success: false };
    const inputsPayload = await buildInputsPayload();
    if (!inputsPayload) return { success: false };

    const payload = { inputs: inputsPayload, finalize: true };
    const res = await PropertyApi.updateOnboardingPost(
      payload,
      onboardingPostId
    );

    if (!res?.data?.success) {
      toast.error(res?.data?.message || "Failed to update post.");
      return { success: false };
    }
    return { success: true };
  };

  const handleUpdateAndGenerateDesign = async () => {
    setProcessingModalOpen(true);
    const { success } = await handleApiSubmit();
    setProcessingModalOpen(false);

    if (success) {
      setIsGenerating(true);
      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetOnboardingPostById(
            onboardingPostId!
          );
          const postData = check?.data?.data;
          const refs = [
            postData?.image_post_design_activity_ref,
            postData?.story_post_design_activity_ref,
          ].filter(Boolean);
          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );
          if (firstWithImage) {
            const assetUrl = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
            setTemplateData((prev: any) => ({
              ...prev,
              generatedImage: assetUrl,
            }));
            setIsGenerating(false);
            toast.success("Design generated successfully!");
          } else {
            setTimeout(pollDesignStatus, pollInterval);
          }
        } catch (err) {
          console.error("Polling error:", err);
          setIsGenerating(false);
        }
      };
      pollDesignStatus();
    }
  };

  const resetInputs = () => {
    fetchTemplate();
  };

  const getPreviewImage = () =>
    templateData?.generatedImage ||
    (templateData?.data?.template_image
      ? `${API_BASE_URL}/${templateData.data.template_image}`
      : "/images/image-video-generation-error-fallback-image-for-card.png");

  const getPreviewContainerClass = (postType: string | null) =>
    postType === CommonEnums.postType.story_post.key
      ? "story-post-preview"
      : "image-post-preview";

  const previewImages: string[] = [getPreviewImage()];

  const hasInputChanges = React.useMemo(() => {
    if (
      !onboardingPost?.inputs ||
      Object.keys(onboardingPost.inputs).length === 0
    ) {
      return true;
    }

    return Object.keys(formValues).some((key) => {
      const originalInput = onboardingPost.inputs[key];
      const currentInput = formValues[key];

      const originalValue = originalInput?.value || "";
      const currentValue = currentInput?.value || "";
      const originalEnabled = originalInput?.enabled ?? true;
      const currentEnabled = currentInput?.enabled ?? true;

      return (
        originalValue !== currentValue || originalEnabled !== currentEnabled
      );
    });
  }, [onboardingPost?.inputs, formValues]);

  if (loading) {
    return (
      <ProtectedComponent>
        <NavigationLayout>
          <div className="template-inputs-wrapper">
            <div className="list-page-loading">
              <Loading />
            </div>
          </div>
        </NavigationLayout>
      </ProtectedComponent>
    );
  }

  const isReadOnly = onboardingPost?.user_type === "agent";

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-onboarding-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() => {
                const subscriptionType = searchParams.get("subscription_type");
                const backUrl = subscriptionType
                  ? `${clientRoutes.AgencyOnboardingPostList}?subscription_type=${subscriptionType}`
                  : clientRoutes.AgencyOnboardingPostList;

                navigate(backUrl);
              }}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
          </div>

          <div className="header-row">
            <div className="page-title">Onboarding Post</div>
            <div className="action-menu-icon-wrapper">
              {onboardingPost?._id && (
                <OnBoardingListMenuIcon
                  postId={onboardingPost._id}
                  onUpdated={() => fetchTemplate()}
                  onPreview={() => setPreviewOpen(true)}
                  previewImageUrl={previewImages[0]}
                />
              )}
            </div>
          </div>

          <div className="onboarding-top-info">
            <div className="top-row">
              <div className="info-item">
                <label>Onboarding Post Id</label>
                <p>{onboardingPost?._id || "-"}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p
                  className={`status-badge ${onboardingPost?.post_status.toLowerCase()}`}
                >
                  {getStatusBadge(onboardingPost?.post_status || null)}
                </p>
              </div>
              {onboardingPost?.user_type === "agent" &&
                onboardingPost?.agent_ref && (
                  <div className="info-item">
                    <label>Agent</label>
                    <p
                      className="agent-name agent-link"
                      onClick={() =>
                        navigate(
                          `/agents/${onboardingPost?.agent_ref?._id}?type=info`
                        )
                      }
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      {onboardingPost?.agent_ref?.principal_agent_first_name}{" "}
                      {onboardingPost?.agent_ref?.principal_agent_last_name}
                    </p>
                  </div>
                )}
              <div className="info-item">
                <label>Post Type</label>
                <p>{formatPostType(onboardingPost?.post_type || "")}</p>
              </div>
              {onboardingPost?.user_type === "agency" && (
                <div className="info-item">
                  <label>Schedule Datetime</label>
                  <p>
                    {onboardingPost?.schedule_datetime
                      ? formatDate(onboardingPost.schedule_datetime)
                      : "Not scheduled"}
                  </p>
                </div>
              )}
            </div>

            <div className="bottom-row">
              {onboardingPost?.user_type === "agent" &&
                onboardingPost?.agent_ref && (
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {onboardingPost?.schedule_datetime
                        ? formatDate(onboardingPost.schedule_datetime)
                        : "Not scheduled"}
                    </p>
                  </div>
                )}
              <div className="info-item">
                <label>Created On</label>
                <p>
                  {onboardingPost ? formatDate(onboardingPost.createdAt) : "-"}
                </p>
              </div>
              <div className="info-item">
                <label>Last Updated On</label>
                <p>
                  {onboardingPost ? formatDate(onboardingPost.updatedAt) : "-"}
                </p>
              </div>
            </div>
          </div>

          <div className="custom-tabs">
            <div
              className={`tab-item ${activeTab === "info" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("info");
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  step: "info",
                });
              }}
            >
              Info
            </div>

            <div
              className={`tab-item ${
                activeTab === "postConfig" ? "active" : ""
              }`}
              onClick={() => {
                setActiveTab("postConfig");
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  step: "configure_post",
                });
              }}
            >
              Post Configuration
            </div>
          </div>

          {activeTab === "info" && (
            <>
              {isLoading ? (
                <div className="list-page-loading">
                  <Loading />
                </div>
              ) : (
                <div className="template-inputs-grid">
                  <div className="template-input-fields">
                    <div className="section-heading">Inputs</div>
                    <div className="input-description">
                      Feel free to update the inputs to customize the onboarding
                      post
                    </div>

                    {(() => {
                      const specificImageOrder = [
                        "bg_image",
                        "placeholder_image_top",
                        "placeholder_image_bottom",
                      ];
                      const imageInputs = inputOrder.filter(
                        (key) => inputs[key]?.input_type === "image"
                      );
                      const textInputs = inputOrder.filter(
                        (key) => inputs[key]?.input_type !== "image"
                      );
                      const orderedInputs = [
                        ...imageInputs.sort(
                          (a, b) =>
                            specificImageOrder.indexOf(a) -
                            specificImageOrder.indexOf(b)
                        ),
                        ...textInputs,
                      ];

                      return orderedInputs.map((key) => {
                        const input = inputs[key] || formValues[key];
                        if (!input) return null;

                        const formValueItem = formValues[key];
                        if (!formValueItem) return null;

                        const value = formValueItem.value || "";
                        const charLimit = formValueItem.characters_limit || 100;
                        const isEnabled = formValueItem.enabled ?? true;
                        const isEditing = imageEditing[key] || false;

                        if (input.input_type === "image") {
                          return (
                            <div className="input-field-block" key={key}>
                              <div className="image-block">
                                <div className="employee-image-header">
                                  <label className="employee-input-label">
                                    {input.label}
                                  </label>
                                  {!isReadOnly && (
                                    <button
                                      className={
                                        isEditing
                                          ? "cancel-image-btn"
                                          : "change-image-btn"
                                      }
                                      type="button"
                                      onClick={() =>
                                        setImageEditing((p) => ({
                                          ...p,
                                          [key]: !p[key],
                                        }))
                                      }
                                    >
                                      {isEditing ? "Cancel" : "Change Image"}
                                    </button>
                                  )}
                                </div>

                                {!isEditing && (
                                  <>
                                    {isImageLoading[key] ? (
                                      <div className="employee-image-loading-preview">
                                        <div className="spinner"></div>
                                      </div>
                                    ) : (
                                      value && (
                                        <div className="employee-image-preview">
                                          <img
                                            src={value}
                                            alt={input.label}
                                            className="employee-image"
                                            onError={(e) => {
                                              e.currentTarget.src =
                                                "/images/image-video-generation-error-fallback-image-for-card.png";
                                            }}
                                          />
                                        </div>
                                      )
                                    )}
                                  </>
                                )}

                                {isEditing && (
                                  <div
                                    className={`employee-image-upload ${
                                      draggedOverKey === key ? "drag-over" : ""
                                    }`}
                                    onClick={() =>
                                      fileInputRefs.current[key]?.click()
                                    }
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleFileUpdate(
                                        e.dataTransfer.files?.[0],
                                        key
                                      );
                                      setDraggedOverKey(null);
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragEnter={(e) => {
                                      e.preventDefault();
                                      setDraggedOverKey(key);
                                    }}
                                    onDragLeave={(e) => {
                                      e.preventDefault();
                                      setDraggedOverKey(null);
                                    }}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      ref={(el) =>
                                        (fileInputRefs.current[key] = el)
                                      }
                                      style={{ display: "none" }}
                                      onChange={(e) =>
                                        handleFileUpdate(
                                          e.target.files?.[0],
                                          key
                                        )
                                      }
                                    />
                                    <div className="upload-icon">
                                      <Upload />
                                    </div>
                                    <p>Drop or Select file</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="input-field-block" key={key}>
                            <div className="input-label-row">
                              <label className="input-label">
                                {input.label} ({value?.length || 0}/{charLimit})
                              </label>

                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  disabled={isReadOnly}
                                  onChange={(e) =>
                                    !isReadOnly &&
                                    setFormValues((p) => ({
                                      ...p,
                                      [key]: {
                                        ...p[key],
                                        enabled: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                                <span className="slider round"></span>
                              </label>
                            </div>

                            <textarea
                              className="input-textarea"
                              maxLength={charLimit}
                              disabled={!isEnabled || isReadOnly}
                              value={value}
                              onChange={(e) =>
                                !isReadOnly &&
                                setFormValues((p) => ({
                                  ...p,
                                  [key]: {
                                    ...p[key],
                                    value: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        );
                      });
                    })()}

                    {!isReadOnly && (
                      <div className="action-text-buttons">
                        <span
                          className={clsx("text-button", {
                            disabled: isGenerating,
                          })}
                          onClick={() => !isGenerating && resetInputs()}
                        >
                          Reset Inputs
                        </span>
                        <span
                          className={clsx("text-button", {
                            disabled: isGenerating,
                          })}
                          onClick={() =>
                            !isGenerating && handleUpdateAndGenerateDesign()
                          }
                        >
                          Update & Generate New Design
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="template-preview">
                    <div className="section-heading">Post Preview</div>
                    <div className="template-preview-label">
                      <span>Preview of design template</span>
                      <div className="preview-actions">
                        <span
                          className={clsx("preview-link", {
                            disabled: isGenerating,
                          })}
                          onClick={() => !isGenerating && setPreviewOpen(true)}
                        >
                          Preview
                        </span>
                        {onboardingPost?.user_type !== "agent" && (
                        <span
                          className={clsx("preview-link", {
                            disabled: isGenerating,
                          })}
                          onClick={() =>
                            !isGenerating && setIsUpdateTemplateOpen(true)
                          }
                        >
                          Change Template
                        </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`onboarding-image-section ${getPreviewContainerClass(
                        postTypeFromUrl
                      )} ${isGenerating ? "loading" : ""}`}
                    >
                      {isGenerating ? (
                        <div className="post-preview-loading">
                          <div className="spinner" />
                          <h4>Your request is being processed.</h4>
                          <p>
                            We're working on your request! Creating a
                            high-quality output can take a little while. We
                            appreciate your patience.
                          </p>
                          <p className="message">
                            Feel free to wait here, or browse other pages. We'll
                            send you a notification when your request is
                            completed.
                          </p>
                        </div>
                      ) : (
                        <img
                          src={getPreviewImage()}
                          alt="Template Preview"
                          className="activity-thumbnail"
                          onError={(e) => {
                            e.currentTarget.src =
                              "/images/image-video-generation-error-fallback-image-for-card.png";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isReadOnly && (
                <>
                  <div className="footer-divider" />
                  <div className="footer-buttons">
                    <button
                      className="btn btn-secondary-changes"
                      type="button"
                      onClick={() => navigate(-1)}
                      disabled={isGenerating}
                    >
                      Discard Changes
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleUpdateAndGenerateDesign}
                      disabled={isGenerating || !hasInputChanges}
                    >
                      {isGenerating ? (
                        <CircularProgress
                          size={24}
                          style={{ color: "white" }}
                        />
                      ) : hasInputChanges ? (
                        "Update Design"
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {isUpdateTemplateOpen && (
            <UpdateTemplateModal
              open={isUpdateTemplateOpen}
              onClose={() => setIsUpdateTemplateOpen(false)}
              selectedDesignTemplateId={getSelectedTemplateId(
                postTypeFromUrl ?? undefined,
                onboardingPost
              )}
              templateTypeId={resolveOnboardingTemplateType(
                postTypeFromUrl || onboardingPost?.post_type,
                listingType
              )}
              subscriptionType="agent_marketing"
              postType={postTypeFromUrl ?? undefined}
              listingType={listingType}
              onConfirm={async () => {
                if (!onboardingPostId) {
                  toast.error("Missing onboarding post ID");
                  return;
                }

                try {
                  setProcessingModalOpen(true);

                  const designTemplateId = await fetchOnboardingTemplateId(
                    subscriptionType,
                    listingType,
                    postTypeFromUrl
                  );

                  if (!designTemplateId) {
                    toast.error("No matching template found!");
                    setIsLoading(false);
                    return;
                  }

                  const payload: Record<string, any> = {
                    inputs: {},
                    image_post_design_activity_ref: null,
                    story_post_design_activity_ref: null,
                    finalize: "true",
                  };

                  const res = await PropertyApi.updateOnboardingPost(
                    payload,
                    onboardingPostId
                  );

                  if (!res?.data?.success) {
                    toast.error(
                      res?.data?.message || "Failed to update onboarding post"
                    );
                    return;
                  }

                  setIsUpdateTemplateOpen(false);
                  setProcessingModalOpen(false);

                  toast.success("Template updated! Generating new design...");

                  await fetchTemplate(designTemplateId);
                  setIsGenerating(true);

                  const pollInterval = 3000;

                  const pollStatus = async () => {
                    try {
                      const check = await PropertyApi.GetOnboardingPostById(
                        onboardingPostId
                      );
                      const postData = check?.data?.data;

                      const refs = [
                        postData?.image_post_design_activity_ref,
                        postData?.story_post_design_activity_ref,
                      ].filter(Boolean);

                      const firstWithImage = refs.find(
                        (ref) => ref?.design_task_id?.output_image_path
                      );

                      if (firstWithImage) {
                        const assetUrl = CommonHelpers.getImageUrlFromImageTool(
                          {
                            path: firstWithImage.design_task_id
                              .output_image_path,
                          }
                        );

                        setTemplateData((prev: any) => ({
                          ...prev,
                          generatedImage: assetUrl,
                        }));

                        setIsGenerating(false);
                        toast.success("Design generated successfully!");
                      } else {
                        setTimeout(pollStatus, pollInterval);
                      }
                    } catch (err) {
                      console.error("Polling error:", err);
                      setIsGenerating(false);
                    }
                  };

                  pollStatus();
                } catch (err) {
                  console.error(err);
                  toast.error(
                    "Something went wrong while updating onboarding post"
                  );
                }
              }}
            />
          )}

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setProcessingModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>

          <PreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={previewImages}
          />

          {activeTab === "postConfig" && <OnboardingPostConfiguration />}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyOnboardingPostView;
